import { useCallback, useEffect, useState } from "react";
import "./components/owner-dashboard.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faBuilding, faArrowTrendUp, faArrowTrendDown, faFileAlt, faCheck, faTimes, faEye, faCog, faTrash, faBars, faGaugeHigh, faKey, faUser, faShieldHalved, faChevronRight, faCircleCheck, faXmark, faRightFromBracket, faClock, faChartBar, faArrowRight, faLightbulb } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../../../i18n/LanguageContext";
import propertyThumbnail from "./components/images/placeholder-property.png";
import { getPropertyImageUrl } from "../../../utils/property-image";
import KycPromptModal from "../kyc-verification/KycPromptModal";
import EmailVerificationBanner from "../../common/EmailVerificationBanner";

const buildLastSixMonthKeys = () => {
  const monthKeys = [];
  const now = new Date();
  for (let i = 5; i >= 0; i -= 1) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    monthKeys.push(`${date.getFullYear()}-${month}`);
  }
  return monthKeys;
};

const trendFromSeries = (series = []) => {
  const current = Number(series[series.length - 1] || 0);
  const previous = Number(series[series.length - 2] || 0);
  const diff = current - previous;

  if (diff === 0) return { direction: "stable", percent: 0, current, previous };
  if (previous === 0) {
    return {
      direction: diff > 0 ? "up" : "down",
      percent: 100,
      current,
      previous,
    };
  }

  return {
    direction: diff > 0 ? "up" : "down",
    percent: (Math.abs(diff) / previous) * 100,
    current,
    previous,
  };
};

const deriveStatsFromData = (props = [], apps = []) => {
  const normalizedProps = Array.isArray(props) ? props : [];
  const normalizedApps = Array.isArray(apps) ? apps : [];
  const monthKeys = buildLastSixMonthKeys();

  const totalProperties = normalizedProps.length;
  const activeListings = normalizedProps.filter(
    (p) => p.isRented && String(p.rentalInfo?.rentalStatus || "").toLowerCase() === "active"
  ).length;
  const listedProperties = normalizedProps.filter(
    (p) => String(p.status || "").toLowerCase() !== "unlisted"
  ).length;
  const unlistedProperties = normalizedProps.filter(
    (p) => String(p.status || "").toLowerCase() === "unlisted"
  ).length;
  const pendingInquiries = normalizedApps.filter(
    (a) => String(a.status || "").toLowerCase() === "pending"
  ).length;

  const estimatedMonthlyRevenue = normalizedProps
    .filter((p) => p.isRented && String(p.rentalInfo?.rentalStatus || "").toLowerCase() === "active")
    .reduce((sum, p) => sum + Number(p.rentalInfo?.monthlyRent || p.price || 0), 0);

  const inquiriesMap = new Map(monthKeys.map((k) => [k, 0]));
  normalizedApps.forEach((app) => {
    if (!app.createdAt) return;
    const created = new Date(app.createdAt);
    if (Number.isNaN(created.getTime())) return;
    const key = `${created.getFullYear()}-${`${created.getMonth() + 1}`.padStart(2, "0")}`;
    if (inquiriesMap.has(key)) inquiriesMap.set(key, Number(inquiriesMap.get(key) || 0) + 1);
  });
  const inquiriesSeries = monthKeys.map((k) => Number(inquiriesMap.get(k) || 0));

  const revenueMap = new Map(monthKeys.map((k) => [k, 0]));
  normalizedProps
    .filter((p) => p.isRented && String(p.rentalInfo?.rentalStatus || "").toLowerCase() === "active")
    .forEach((p) => {
      const dateSource = p.rentalInfo?.startDate;
      if (!dateSource) return;
      const started = new Date(dateSource);
      if (Number.isNaN(started.getTime())) return;
      const key = `${started.getFullYear()}-${`${started.getMonth() + 1}`.padStart(2, "0")}`;
      if (!revenueMap.has(key)) return;
      revenueMap.set(key, Number(revenueMap.get(key) || 0) + Number(p.rentalInfo?.monthlyRent || p.price || 0));
    });
  const revenueSeries = monthKeys.map((k) => Number(revenueMap.get(k) || 0));

  return {
    totalProperties,
    activeListings,
    listedProperties,
    unlistedProperties,
    pendingInquiries,
    estimatedMonthlyRevenue,
    inquiriesSeries,
    revenueSeries,
    inquiriesTrend: trendFromSeries(inquiriesSeries),
    revenueTrend: trendFromSeries(revenueSeries),
  };
};


function OwnerDashboard() {
  const { t } = useLanguage();

  const [ownerName, setOwnerName] = useState("");
  const [stats, setStats] = useState({
    totalProperties: 0,
    activeListings: 0,
    listedProperties: 0,
    unlistedProperties: 0,
    pendingInquiries: 0,
    estimatedMonthlyRevenue: 0,
    inquiriesTrend: { direction: "stable", percent: 0, current: 0, previous: 0 },
    revenueTrend: { direction: "stable", percent: 0, current: 0, previous: 0 },
    inquiriesSeries: [],
    revenueSeries: [],
  });
  const [properties, setProperties] = useState([]);
  const [showTerminationModal, setShowTerminationModal] = useState(false);
  const [selectedTermination, setSelectedTermination] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [showDenyModal, setShowDenyModal] = useState(false);
  const [denyReason, setDenyReason] = useState("");
  const [submittingDeny, setSubmittingDeny] = useState(false);

  // Applications state
  const [applications, setApplications] = useState([]);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [processingApp, setProcessingApp] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [showRejectInput, setShowRejectInput] = useState(false);
  // Dashboard sidebar state
  const [activeSection, setActiveSection] = useState("overview");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [appStatusFilter, setAppStatusFilter] = useState("all");
  const [kycStatus, setKycStatus] = useState(null); // null = loading
  const [previewDocUrl, setPreviewDocUrl] = useState("");
  const [previewDocType, setPreviewDocType] = useState("");
  const [previewDocLabel, setPreviewDocLabel] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showPropertyVerificationModal, setShowPropertyVerificationModal] = useState(false);
  const [verificationProperty, setVerificationProperty] = useState(null);
  const [verificationRequirements, setVerificationRequirements] = useState([]);
  const [verificationSubmitting, setVerificationSubmitting] = useState(false);
  const [verificationForm, setVerificationForm] = useState({
    ownership_certificate: null,
    tax_clearance_certificate: null,
    land_deed_certificate: null,
    utility_bill_certificate: null,
    supportingNotes: "",
  });

  // Template editor state
  const [customQuestions, setCustomQuestions] = useState([]);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [newQuestion, setNewQuestion] = useState({ label: "", type: "text", required: false });

  const navigate = useNavigate();

  // Load KYC status
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    fetch("/api/kyc/status", { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setKycStatus(data.kycStatus || "not_submitted"); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;

    fetch("/api/property-verification/requirements", { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data && Array.isArray(data.requirements)) {
          setVerificationRequirements(data.requirements);
        }
      })
      .catch(() => {});
  }, []);

  const getTrendMeta = (trend) => {
    if (!trend || trend.direction === "stable") {
      return {
        icon: null,
        className: "text-muted",
        label: "0.0%",
      };
    }

    const isUp = trend.direction === "up";
    return {
      icon: isUp ? faArrowTrendUp : faArrowTrendDown,
      className: isUp ? "text-success" : "text-danger",
      label: `${isUp ? "+" : "-"}${Number(trend.percent || 0).toFixed(1)}%`,
    };
  };

  const formatPrice = (value) => {
    if (value == null || isNaN(value)) return "NPR 0";
    return (
      "NPR " +
      Number(value).toLocaleString("en-NP", {
        maximumFractionDigits: 0,
      })
    );
  }

  // Helper function to decode JWT and get user ID
  const getUserIdFromToken = () => {
    const token = localStorage.getItem("token");
    if (!token) return null;
    
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      
      const payload = JSON.parse(jsonPayload);
      return payload.id;
    } catch (error) {
      console.error("Error decoding token:", error);
      return null;
    }
  };

  const loadDashboard = useCallback(async()=>{
    const token = localStorage.getItem("token");
    if(!token) return;
    
    try{
      const [statsRes, propsRes] = await Promise.all([
        fetch("/api/owner/stats", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }).catch(() => null),
        fetch("/api/owner/properties-with-rentals", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }),
      ]);

      if(!propsRes.ok){
        throw new Error("Failed to fetch owner properties");
      }
      const props = await propsRes.json();
      const safeProps = Array.isArray(props) ? props : [];

      let nextStats = null;
      if (statsRes && statsRes.ok) {
        const apiStats = await statsRes.json();
        nextStats = {
          totalProperties: Number(apiStats.totalProperties || 0),
          activeListings: Number(apiStats.activeListings || 0),
          listedProperties: Number(apiStats.listedProperties || 0),
          unlistedProperties: Number(apiStats.unlistedProperties || 0),
          pendingInquiries: Number(apiStats.pendingInquiries || 0),
          estimatedMonthlyRevenue: Number(apiStats.estimatedMonthlyRevenue || apiStats.totalRevenue || 0),
          inquiriesTrend: apiStats.inquiriesTrend || { direction: "stable", percent: 0, current: 0, previous: 0 },
          revenueTrend: apiStats.revenueTrend || { direction: "stable", percent: 0, current: 0, previous: 0 },
          inquiriesSeries: Array.isArray(apiStats.inquiriesSeries) ? apiStats.inquiriesSeries : [],
          revenueSeries: Array.isArray(apiStats.revenueSeries) ? apiStats.revenueSeries : [],
        };
      } else {
        nextStats = deriveStatsFromData(safeProps, []);
      }

      console.log("Loaded properties:", safeProps);
      console.log("First property thumbnail:", safeProps[0]?.thumbnail);

      setStats((prev) => ({ ...prev, ...nextStats }));
      setProperties(safeProps);
    } catch (err){
      console.error("Error loading dashboard data:", err);
    }
  }, []);

  useEffect(() => {
    const raw = (v) => (v && v !== "null" && v !== "undefined" ? v : "");
    const firstname = raw(localStorage.getItem("firstname"));
    const lastname = raw(localStorage.getItem("lastname"));
    if (firstname || lastname) {
      setOwnerName(`${firstname} ${lastname}`.trim());
    } else {
      setOwnerName("Property Owner");
    }

    const token = localStorage.getItem("token");
    if(!token){
      console.warn("No token found, cannot fetch owner stats.");
      return;
    }

    // Get user ID from token
    const userId = getUserIdFromToken();
    setCurrentUserId(userId);

    loadDashboard();
    fetchApplications();
    fetchTemplate();
  }, [loadDashboard]);

  const fetchApplications = async () => {
    const token = localStorage.getItem("token");
    if (!token) return;
    try {
      const res = await fetch("/api/owner/applications", { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const appRows = await res.json();
        setApplications(appRows);
      }
    } catch (err) { console.error("Error fetching applications:", err); }
  };

  const fetchTemplate = async () => {
    const token = localStorage.getItem("token");
    if (!token) return;
    try {
      const res = await fetch("/api/owner/application-template", { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setCustomQuestions(Array.isArray(data.customQuestions) ? data.customQuestions : []);
      }
    } catch (err) { console.error("Error fetching template:", err); }
  };

  const handleApproveApplication = async (appId) => {
    setProcessingApp(true);
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`/api/applications/${appId}/approve`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        alert("Application approved! Rental has been created.");
        setShowApplicationModal(false);
        fetchApplications();
        loadDashboard();
      } else {
        alert(data.message || "Failed to approve application");
      }
    } catch (err) {
      alert("Error connecting to server");
    } finally {
      setProcessingApp(false);
    }
  };

  const handleRejectApplication = async (appId) => {
    if (!rejectReason.trim()) { alert("Please provide a rejection reason"); return; }
    setProcessingApp(true);
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`/api/applications/${appId}/reject`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ reason: rejectReason }),
      });
      const data = await res.json();
      if (res.ok) {
        alert("Application rejected.");
        setShowApplicationModal(false);
        setRejectReason("");
        setShowRejectInput(false);
        fetchApplications();
      } else {
        alert(data.message || "Failed to reject application");
      }
    } catch (err) {
      alert("Error connecting to server");
    } finally {
      setProcessingApp(false);
    }
  };

  const handleSaveTemplate = async () => {
    setSavingTemplate(true);
    const token = localStorage.getItem("token");
    try {
      const res = await fetch("/api/owner/application-template", {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ customQuestions }),
      });
      if (res.ok) {
        alert("Application template saved!");
      } else {
        alert("Failed to save template");
      }
    } catch (err) {
      alert("Error saving template");
    } finally {
      setSavingTemplate(false);
    }
  };

  const verificationStatusFromProperty = (property) => {
    return String(property?.verificationStatus || property?.verification_status || "unverified").toLowerCase();
  };

  const openPropertyVerificationModal = (property) => {
    setVerificationProperty(property);
    setVerificationForm({
      ownership_certificate: null,
      tax_clearance_certificate: null,
      land_deed_certificate: null,
      utility_bill_certificate: null,
      supportingNotes: "",
    });
    setShowPropertyVerificationModal(true);
  };

  const handlePropertyVerificationFile = (field, file) => {
    setVerificationForm((prev) => ({ ...prev, [field]: file || null }));
  };

  const submitPropertyVerification = async () => {
    if (!verificationProperty) return;
    if (!verificationForm.ownership_certificate || !verificationForm.tax_clearance_certificate) {
      alert("Please upload ownership certificate and tax/ward clearance documents.");
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) return;

    const formData = new FormData();
    formData.append("ownership_certificate", verificationForm.ownership_certificate);
    formData.append("tax_clearance_certificate", verificationForm.tax_clearance_certificate);
    if (verificationForm.land_deed_certificate) formData.append("land_deed_certificate", verificationForm.land_deed_certificate);
    if (verificationForm.utility_bill_certificate) formData.append("utility_bill_certificate", verificationForm.utility_bill_certificate);
    if (verificationForm.supportingNotes.trim()) formData.append("supportingNotes", verificationForm.supportingNotes.trim());

    setVerificationSubmitting(true);
    try {
      const response = await fetch(`/api/owner/properties/${verificationProperty.id || verificationProperty._id}/verification-request`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      const data = await response.json();
      if (!response.ok) {
        alert(data.message || "Unable to submit property verification request.");
        return;
      }

      alert("Verification request submitted. Our admin team will review your property documents.");
      setShowPropertyVerificationModal(false);
      setVerificationProperty(null);
      loadDashboard();
    } catch (error) {
      alert("Unable to submit property verification request right now.");
    } finally {
      setVerificationSubmitting(false);
    }
  };

  const addCustomQuestion = () => {
    if (!newQuestion.label.trim()) { alert("Question label is required"); return; }
    setCustomQuestions((prev) => [...prev, { ...newQuestion, id: `cq_${Date.now()}` }]);
    setNewQuestion({ label: "", type: "text", required: false });
  };

  const removeCustomQuestion = (idx) => {
    setCustomQuestions((prev) => prev.filter((_, i) => i !== idx));
  };

  const closeDocumentPreview = () => {
    if (previewDocUrl) URL.revokeObjectURL(previewDocUrl);
    setPreviewDocUrl("");
    setPreviewDocType("");
    setPreviewDocLabel("");
  };

  const getDocumentType = (filename = "", mime = "") => {
    const lowerMime = mime.toLowerCase();
    const lowerName = filename.toLowerCase();
    if (lowerMime.startsWith("image/") || /\.(jpg|jpeg|png|gif|webp)$/i.test(lowerName)) return "image";
    if (lowerMime === "application/pdf" || lowerName.endsWith(".pdf")) return "pdf";
    return "other";
  };

  const handlePreviewDocument = async (field, filename) => {
    const token = localStorage.getItem("token");
    if (!token || !filename) return;

    setPreviewLoading(true);
    try {
      const res = await fetch(`/uploads/applications/${filename}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "Failed to load document");
      }

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      if (previewDocUrl) URL.revokeObjectURL(previewDocUrl);

      setPreviewDocUrl(url);
      setPreviewDocType(getDocumentType(filename, blob.type));
      setPreviewDocLabel(field.replace(/_/g, " "));
    } catch (err) {
      console.error("Document preview error:", err);
      alert("Unable to load document preview");
    } finally {
      setPreviewLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (previewDocUrl) URL.revokeObjectURL(previewDocUrl);
    };
  }, [previewDocUrl]);

  useEffect(() => {
    const fallback = deriveStatsFromData(properties, applications);
    setStats((prev) => ({
      ...prev,
      totalProperties: prev.totalProperties || fallback.totalProperties,
      activeListings: prev.activeListings || fallback.activeListings,
      listedProperties: prev.listedProperties || fallback.listedProperties,
      unlistedProperties: prev.unlistedProperties || fallback.unlistedProperties,
      pendingInquiries: prev.pendingInquiries || fallback.pendingInquiries,
      estimatedMonthlyRevenue: prev.estimatedMonthlyRevenue || fallback.estimatedMonthlyRevenue,
      inquiriesSeries: (prev.inquiriesSeries && prev.inquiriesSeries.some((v) => Number(v) > 0))
        ? prev.inquiriesSeries
        : fallback.inquiriesSeries,
      revenueSeries: (prev.revenueSeries && prev.revenueSeries.some((v) => Number(v) > 0))
        ? prev.revenueSeries
        : fallback.revenueSeries,
      inquiriesTrend: (prev.inquiriesTrend && (prev.inquiriesTrend.current || prev.inquiriesTrend.previous))
        ? prev.inquiriesTrend
        : fallback.inquiriesTrend,
      revenueTrend: (prev.revenueTrend && (prev.revenueTrend.current || prev.revenueTrend.previous))
        ? prev.revenueTrend
        : fallback.revenueTrend,
    }));
  }, [properties, applications]);

  const handleApproveTermination = async (rentalId) => {
    if (!window.confirm("Are you sure you want to approve this termination request? This will end the lease immediately.")) {
      return;
    }

    const token = localStorage.getItem("token");

    try {
      const response = await fetch(`/api/rentals/${rentalId}/approve-termination`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok) {
        alert("Lease terminated successfully!");
        setShowTerminationModal(false);
        loadDashboard();
      } else {
        alert(data.message || "Failed to approve termination");
      }
    } catch (error) {
      console.error("Error approving termination:", error);
      alert("Error connecting to server");
    }
  };

  const handleCancelTermination = async (rentalId) => {
    if (!window.confirm("Are you sure you want to cancel this termination request?")) {
      return;
    }

    const token = localStorage.getItem("token");

    try {
      const response = await fetch(`/api/rentals/${rentalId}/cancel-termination`, {
        method: "DELETE",
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok) {
        alert("Termination request cancelled");
        setShowTerminationModal(false);
        loadDashboard();
      } else {
        alert(data.message || "Failed to cancel termination");
      }
    } catch (error) {
      console.error("Error cancelling termination:", error);
      alert("Error connecting to server");
    }
  };

  const handleDenyTermination = async () => {
    if (!denyReason.trim()) {
      alert("Please provide a reason for denying the termination request");
      return;
    }

    setSubmittingDeny(true);
    const token = localStorage.getItem("token");

    try {
      const response = await fetch(`/api/rentals/${selectedTermination.rentalId}/deny-termination`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ reason: denyReason.trim() })
      });

      const data = await response.json();

      if (response.ok) {
        alert("Termination request denied");
        setShowDenyModal(false);
        setShowTerminationModal(false);
        setDenyReason("");
        loadDashboard();
      } else {
        alert(data.message || "Failed to deny termination");
      }
    } catch (error) {
      console.error("Error denying termination:", error);
      alert("Error connecting to server");
    } finally {
      setSubmittingDeny(false);
    }
  };

  const handleViewTerminationRequest = (property) => {
    setSelectedTermination({
      propertyName: property.propertyName || property.name,
      renterName: property.rentalInfo.renterName,
      reason: property.rentalInfo.terminationReason,
      rentalId: property.rentalInfo.rentalId,
      requestedBy: property.rentalInfo.terminationRequestedBy
    });
    setShowTerminationModal(true);
  };

  const handleRequestTermination = async (rentalId) => {
    const reason = prompt("Please provide a reason for termination:");
    if (!reason || !reason.trim()) {
      return;
    }

    const token = localStorage.getItem("token");

    try {
      const response = await fetch(`/api/rentals/${rentalId}/request-termination`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ reason: reason.trim() })
      });

      const data = await response.json();

      if (response.ok) {
        alert("Termination request submitted successfully!");
        loadDashboard();
      } else {
        alert(data.message || "Failed to submit termination request");
      }
    } catch (error) {
      console.error("Error requesting termination:", error);
      alert("Error connecting to server");
    }
  };

  // Sidebar navigation helper
  const navTo = (section) => {
    setActiveSection(section);
    setSidebarOpen(false);
  };

  const pendingAppsCount = applications.filter((a) => a.status === "pending").length;
  const rentedProperties = properties.filter((p) => p.isRented);
  const filteredApps =
    appStatusFilter === "all"
      ? applications
      : applications.filter((a) => a.status === appStatusFilter);

  const sectionTitles = {
    overview: t("ownerDashboard.dashboard") || "Dashboard",
    properties: t("ownerDashboard.propertiesTab") || "Properties",
    applications: t("ownerDashboard.applicationsTab") || "Applications",
    rentals: t("ownerDashboard.activeRentalsSection") || "Active Rentals",
    appform: t("ownerDashboard.appFormSection") || "Application Form",
    settings: t("ownerDashboard.accountSettings") || "Account Settings",
  };

  return (
    <div className="od-shell">

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="od-overlay" onClick={() => setSidebarOpen(false)} aria-hidden="true" />
      )}

      {/* â”€â”€â”€ Sidebar â”€â”€â”€ */}
      <aside className={`od-sidebar${sidebarOpen ? " od-sidebar--open" : ""}`}>
        <div className="od-sidebar-brand">
          <span className="od-brand-text">GharBhada</span>
          <button className="od-sidebar-close" onClick={() => setSidebarOpen(false)} aria-label="Close menu">
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        <div className="od-owner-meta">
          <div className="od-avatar-circle">{ownerName.charAt(0).toUpperCase() || "O"}</div>
          <div className="od-owner-info">
            <div className="od-owner-name">{ownerName}</div>
            <div className="od-owner-role">{t("ownerDashboard.propertyOwnerRole") || "Property Owner"}</div>
          </div>
        </div>

        <nav className="od-nav" aria-label="Dashboard navigation">
          <span className="od-nav-label">{t("ownerDashboard.mainNavLabel") || "Main"}</span>
          <button className={`od-nav-item${activeSection === "overview" ? " od-nav-item--active" : ""}`} onClick={() => navTo("overview")}>
            <FontAwesomeIcon icon={faGaugeHigh} className="od-nav-icon" />
            <span>{t("ownerDashboard.overviewSection") || "Overview"}</span>
          </button>
          <button className={`od-nav-item${activeSection === "properties" ? " od-nav-item--active" : ""}`} onClick={() => navTo("properties")}>
            <FontAwesomeIcon icon={faBuilding} className="od-nav-icon" />
            <span>{t("ownerDashboard.propertiesTab")}</span>
            {stats.totalProperties > 0 && <span className="od-nav-count">{stats.totalProperties}</span>}
          </button>
          <button className={`od-nav-item${activeSection === "applications" ? " od-nav-item--active" : ""}`} onClick={() => navTo("applications")}>
            <FontAwesomeIcon icon={faFileAlt} className="od-nav-icon" />
            <span>{t("ownerDashboard.applicationsTab")}</span>
            {pendingAppsCount > 0 && <span className="od-nav-badge">{pendingAppsCount}</span>}
          </button>
          <button className={`od-nav-item${activeSection === "rentals" ? " od-nav-item--active" : ""}`} onClick={() => navTo("rentals")}>
            <FontAwesomeIcon icon={faKey} className="od-nav-icon" />
            <span>{t("ownerDashboard.activeRentalsSection") || "Active Rentals"}</span>
            {rentedProperties.length > 0 && <span className="od-nav-count">{rentedProperties.length}</span>}
          </button>
          <span className="od-nav-label" style={{ marginTop: "8px" }}>{t("ownerDashboard.configNavLabel") || "Configuration"}</span>
          <button className={`od-nav-item${activeSection === "appform" ? " od-nav-item--active" : ""}`} onClick={() => navTo("appform")}>
            <FontAwesomeIcon icon={faCog} className="od-nav-icon" />
            <span>{t("ownerDashboard.appFormSection") || "Application Form"}</span>
          </button>
          <button className={`od-nav-item${activeSection === "settings" ? " od-nav-item--active" : ""}`} onClick={() => navTo("settings")}>
            <FontAwesomeIcon icon={faUser} className="od-nav-icon" />
            <span>{t("ownerDashboard.accountSettings") || "Account Settings"}</span>
          </button>
        </nav>

        <div className="od-sidebar-footer">
          <button className="od-sidebar-add-btn" onClick={() => navigate("/list-property")}>
            <FontAwesomeIcon icon={faPlus} />
            <span>{t("ownerDashboard.addProperty") || "Add Property"}</span>
          </button>
        </div>
      </aside>

      {/* â”€â”€â”€ Main area â”€â”€â”€ */}
      <div className="od-main">
        {/* Top bar */}
        <header className="od-topbar">
          <button className="od-hamburger" onClick={() => setSidebarOpen(true)} aria-label="Open menu">
            <FontAwesomeIcon icon={faBars} />
          </button>
          <h1 className="od-topbar-title">{sectionTitles[activeSection]}</h1>
          <div className="od-topbar-right">
            {kycStatus && kycStatus !== "approved" && (
              <button className="od-kyc-chip od-kyc-chip--warn" onClick={() => navigate("/kyc-verification")}>
                <FontAwesomeIcon icon={faShieldHalved} />
                <span>{kycStatus === "pending" ? t("ownerDashboard.kycPendingChip") || "KYC Pending" : t("ownerDashboard.verifyIdentityChip") || "Verify Identity"}</span>
              </button>
            )}
            {kycStatus === "approved" && (
              <span className="od-kyc-chip od-kyc-chip--ok">
                <FontAwesomeIcon icon={faCircleCheck} />
                <span>{t("ownerDashboard.verifiedChip") || "Verified"}</span>
              </span>
            )}
            <button className="od-topbar-add-btn" onClick={() => navigate("/list-property")}>
              <FontAwesomeIcon icon={faPlus} />
<span>{t("ownerDashboard.addProperty") || "Add Property"}</span>
            </button>
          </div>
        </header>

        {/* System banners */}
        <div className="od-banners">
          <KycPromptModal kycStatus={kycStatus} role="owner" />
          <EmailVerificationBanner />
        </div>

        {/* Content */}
        <main className="od-content">

          {/* â”€â”€ OVERVIEW â”€â”€ */}
          {activeSection === "overview" && (
            <div className="od-section">
              <div className="od-greeting">
                <h2 className="od-greeting-title">{t("ownerDashboard.greetingPrefix") || "Welcome back"}, {ownerName.split(" ")[0] || "there"} 👋</h2>
                <p className="od-greeting-sub">{t("ownerDashboard.greetingSnapshot") || "Here's a snapshot of your properties and activity."}</p>
              </div>

              {/* Metric cards */}
              <div className="od-metrics-grid">
                {/* Total Properties Card */}
                <button 
                  className="od-metric-card od-metric-card--clickable" 
                  onClick={() => navTo("properties")}
                  title="Click to view all properties"
                >
                  <div className="od-metric-icon od-metric-icon--blue"><FontAwesomeIcon icon={faBuilding} /></div>
                  <div className="od-metric-body">
                    <span className="od-metric-label">{t("ownerDashboard.totalProperties")}</span>
                    <span className="od-metric-value">{stats.totalProperties}</span>
                    <span className="od-metric-sub">{stats.listedProperties} {t("ownerDashboard.listedLabel") || "listed"} &middot; {stats.unlistedProperties} {t("ownerDashboard.unlistedLabel") || "unlisted"}</span>
                  </div>
                  <div className="od-metric-cta"><FontAwesomeIcon icon={faChevronRight} /></div>
                </button>

                {/* Active Rentals Card */}
                <button 
                  className="od-metric-card od-metric-card--clickable" 
                  onClick={() => navTo("rentals")}
                  title="Click to view active rentals"
                >
                  <div className="od-metric-icon od-metric-icon--green"><FontAwesomeIcon icon={faKey} /></div>
                  <div className="od-metric-body">
                    <span className="od-metric-label">{t("ownerDashboard.activeRentals") || "Active Rentals"}</span>
                    <span className="od-metric-value">{stats.activeListings}</span>
                    <span className="od-metric-sub">{t("ownerDashboard.currentlyOccupied") || "Currently occupied"}</span>
                  </div>
                  <div className="od-metric-cta"><FontAwesomeIcon icon={faChevronRight} /></div>
                </button>

                {/* Pending Inquiries Card */}
                <button 
                  className="od-metric-card od-metric-card--clickable" 
                  onClick={() => { setAppStatusFilter("pending"); navTo("applications"); }}
                  title="Click to review pending applications"
                >
                  <div className="od-metric-icon od-metric-icon--orange"><FontAwesomeIcon icon={faFileAlt} /></div>
                  <div className="od-metric-body">
                    <span className="od-metric-label">{t("ownerDashboard.pendingInquiries")}</span>
                    <span className="od-metric-value">{stats.pendingInquiries}</span>
                    <div className="od-sparkline">
                      {stats.inquiriesSeries.map((v, i) => {
                        const max = Math.max(...stats.inquiriesSeries, 1);
                        return <span key={i} className="od-spark-bar" style={{ height: Math.max(4, Math.round((v / max) * 28)) }} />;
                      })}
                    </div>
                    {(() => { const meta = getTrendMeta(stats.inquiriesTrend); return (<span className={`od-metric-trend ${meta.className}`}>{meta.icon && <FontAwesomeIcon icon={meta.icon} className="me-1" />}{meta.label}</span>); })()}
                  </div>
                  <div className="od-metric-cta"><FontAwesomeIcon icon={faChevronRight} /></div>
                </button>

                {/* Estimated Revenue Card */}
                <button 
                  className="od-metric-card od-metric-card--clickable" 
                  onClick={() => navTo("rentals")}
                  title="Click to view rental details"
                >
                  <div className="od-metric-icon od-metric-icon--purple"><FontAwesomeIcon icon={faArrowTrendUp} /></div>
                  <div className="od-metric-body">
                    <span className="od-metric-label">{t("ownerDashboard.estRevenue")}</span>
                    <span className="od-metric-value">NPR {stats.estimatedMonthlyRevenue.toLocaleString("en-NP", { maximumFractionDigits: 0 })}</span>
                    <div className="od-sparkline">
                      {stats.revenueSeries.map((v, i) => {
                        const max = Math.max(...stats.revenueSeries, 1);
                        return <span key={i} className="od-spark-bar od-spark-bar--purple" style={{ height: Math.max(4, Math.round((v / max) * 28)) }} />;
                      })}
                    </div>
                    {(() => { const meta = getTrendMeta(stats.revenueTrend); return (<span className={`od-metric-trend ${meta.className}`}>{meta.icon && <FontAwesomeIcon icon={meta.icon} className="me-1" />}{meta.label}</span>); })()}
                  </div>
                  <div className="od-metric-cta"><FontAwesomeIcon icon={faChevronRight} /></div>
                </button>
              </div>

              {/* Quick actions */}
              <div className="od-quick-grid">
                <button className="od-quick-card" onClick={() => navTo("properties")}>
                  <FontAwesomeIcon icon={faBuilding} className="od-quick-icon" />
                  <span>{t("ownerDashboard.manageProperties") || "Manage Properties"}</span>
                  <FontAwesomeIcon icon={faChevronRight} className="od-quick-arrow" />
                </button>
                <button className="od-quick-card" onClick={() => navTo("applications")}>
                  <FontAwesomeIcon icon={faFileAlt} className="od-quick-icon" />
                  <span>{t("ownerDashboard.reviewApplications") || "Review Applications"}</span>
                  {pendingAppsCount > 0 && <span className="od-quick-badge">{pendingAppsCount} {t("ownerDashboard.pendingBadge") || "pending"}</span>}
                  <FontAwesomeIcon icon={faChevronRight} className="od-quick-arrow" />
                </button>
                <button className="od-quick-card" onClick={() => navTo("rentals")}>
                  <FontAwesomeIcon icon={faKey} className="od-quick-icon" />
                  <span>{t("ownerDashboard.activeRentalsSection") || "Active Rentals"}</span>
                  <FontAwesomeIcon icon={faChevronRight} className="od-quick-arrow" />
                </button>
                <button className="od-quick-card" onClick={() => navigate("/list-property")}>
                  <FontAwesomeIcon icon={faPlus} className="od-quick-icon" />
                  <span>{t("ownerDashboard.addNewProperty") || "Add New Property"}</span>
                  <FontAwesomeIcon icon={faChevronRight} className="od-quick-arrow" />
                </button>
              </div>

              {/* Portfolio Insights */}
              <div className="od-insights-grid">
                <div className="od-insight-card od-insight-card--primary">
                  <div className="od-insight-header">
                    <h4 className="od-insight-title">Portfolio Overview</h4>
                    <FontAwesomeIcon icon={faChartBar} className="od-insight-icon-top" />
                  </div>
                  <div className="od-insight-stats">
                    <div className="od-stat-item">
                      <span className="od-stat-label">Listed Properties</span>
                      <span className="od-stat-value">{stats.listedProperties}</span>
                    </div>
                    <div className="od-stat-item">
                      <span className="od-stat-label">Occupied</span>
                      <span className="od-stat-value" style={{ color: '#15803d' }}>{stats.activeListings}</span>
                    </div>
                    <div className="od-stat-item">
                      <span className="od-stat-label">Applications</span>
                      <span className="od-stat-value" style={{ color: '#ea580c' }}>{applications.length}</span>
                    </div>
                  </div>
                  <button className="od-insight-btn" onClick={() => navTo("properties")}>
                    View Details <FontAwesomeIcon icon={faArrowRight} className="ms-2" style={{ fontSize: '0.7rem' }} />
                  </button>
                </div>

                <div className="od-insight-card od-insight-card--secondary">
                  <div className="od-insight-header">
                    <h4 className="od-insight-title">Quick Stats</h4>
                    <FontAwesomeIcon icon={faLightbulb} className="od-insight-icon-top" />
                  </div>
                  <div className="od-insight-content">
                    {(() => {
                      const approvedCount = applications.filter(a => a.status === 'approved').length;
                      const approvalRate = applications.length > 0 ? Math.round((approvedCount / applications.length) * 100) : 0;
                      return (
                        <>
                          <div className="od-quick-stat">
                            <span className="od-stat-label">Approval Rate</span>
                            <div className="od-stat-bar-wrap">
                              <div className="od-stat-bar" style={{ width: `${approvalRate}%` }} />
                            </div>
                            <span className="od-stat-value">{approvalRate}%</span>
                          </div>
                          <div className="od-quick-stat">
                            <span className="od-stat-label">Avg Response Time</span>
                            <span className="od-stat-value od-stat-value--highlight">24 hrs</span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>

                <div className="od-insight-card od-insight-card--accent">
                  <div className="od-insight-header">
                    <h4 className="od-insight-title">Monthly Performance</h4>
                    <FontAwesomeIcon icon={faArrowTrendUp} className="od-insight-icon-top" />
                  </div>
                  <div className="od-insight-content">
                    <div className="od-perf-metric">
                      <div className="od-perf-label">Revenue (30 days)</div>
                      <div className="od-perf-value">NPR {stats.estimatedMonthlyRevenue.toLocaleString("en-NP", { maximumFractionDigits: 0 })}</div>
                    </div>
                    <div className="od-perf-trend" style={{ color: stats.revenueTrend.direction === 'up' ? '#15803d' : stats.revenueTrend.direction === 'down' ? '#dc2626' : '#6b7280' }}>
                      {(() => { const meta = getTrendMeta(stats.revenueTrend); return (<>{meta.icon && <FontAwesomeIcon icon={meta.icon} className="me-2" />}{meta.label}</> ); })()}
                    </div>
                  </div>
                </div>
              </div>

              {/* Recent applications */}
              {applications.length > 0 && (
                <div className="od-card">
                  <div className="od-card-header">
                    <h3 className="od-card-title">{t("ownerDashboard.recentApplications") || "Recent Applications"}</h3>
                    <button className="od-card-link" onClick={() => navTo("applications")}>{t("ownerDashboard.viewAll") || "View all \u2192"}</button>
                  </div>
                  <div className="od-app-list">
                    {applications.slice(0, 5).map((app) => (
                      <div key={app.id} className="od-app-row">
                        <div className="od-app-avatar">{(app.renterName || "?").charAt(0).toUpperCase()}</div>
                        <div className="od-app-info">
                          <div className="od-app-name">{app.renterName}</div>
                          <div className="od-app-prop">{app.propertyName}</div>
                        </div>
                        <span className={`od-status-pill od-status-pill--${app.status}`}>
                          {app.status.charAt(0).toUpperCase() + app.status.slice(1)}
                        </span>
                        <button className="od-row-view-btn" onClick={() => { setSelectedApplication(app); setShowApplicationModal(true); setShowRejectInput(false); setRejectReason(""); }}>
                          <FontAwesomeIcon icon={faEye} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* â”€â”€ PROPERTIES â”€â”€ */}
          {activeSection === "properties" && (
            <div className="od-section">
              <div className="od-toolbar">
                <span className="od-toolbar-count">{properties.length} {properties.length === 1 ? t("ownerDashboard.propertySingular") || "property" : t("ownerDashboard.propertyPlural") || "properties"}</span>
                <button className="btn btn-primary btn-sm" onClick={() => navigate("/list-property")}>
                  <FontAwesomeIcon icon={faPlus} className="me-1" /> {t("ownerDashboard.addProperty") || "Add Property"}
                </button>
              </div>
              {properties.length === 0 ? (
                <div className="od-empty-state">
                  <FontAwesomeIcon icon={faBuilding} className="od-empty-icon" />
                  <h4>{t("ownerDashboard.noPropertiesYet") || "No properties yet"}</h4>
                  <p className="text-muted">{t("ownerDashboard.noPropertiesMsg") || "List your first property to start receiving applications."}</p>
                  <button className="btn btn-primary" onClick={() => navigate("/list-property")}>
                    <FontAwesomeIcon icon={faPlus} className="me-1" /> {t("ownerDashboard.listAProperty") || "List a Property"}
                  </button>
                </div>
              ) : (
                <div className="od-property-grid">
                  {properties.map((property) => (
                    <div 
                      key={property._id || property.id} 
                      className="od-property-card od-property-card--clickable"
                      onClick={() => {
                        const id = property._id || property.id;
                        navigate(property.isRented ? `/property-dashboard/${id}` : `/property-settings/${id}`);
                      }}
                      role="button"
                      tabIndex="0"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          const id = property._id || property.id;
                          navigate(property.isRented ? `/property-dashboard/${id}` : `/property-settings/${id}`);
                        }
                      }}
                    >
                      {(() => {
                        const verificationStatus = verificationStatusFromProperty(property);
                        return (
                          <span className={`od-verification-chip od-verification-chip--${verificationStatus}`}>
                            <FontAwesomeIcon icon={verificationStatus === "approved" ? faCircleCheck : verificationStatus === "pending" ? faClock : faShieldHalved} />
                            <span>
                              {verificationStatus === "approved"
                                ? "Verified"
                                : verificationStatus === "pending"
                                  ? "Verification Pending"
                                  : verificationStatus === "rejected"
                                    ? "Verification Rejected"
                                    : "Not Verified"}
                            </span>
                          </span>
                        );
                      })()}
                      <div className="od-property-img-wrap">
                        <img
                          src={property.thumbnail ? getPropertyImageUrl(property.thumbnail, { width: 480 }) : property.photos?.[0] ? getPropertyImageUrl(property.photos[0], { width: 480 }) : propertyThumbnail}
                          alt={property.name || property.propertyName || "Property"}
                          className="od-property-img"
                        />
                        <span className={`od-prop-status-tag ${property.isRented ? "od-prop-status-tag--rented" : property.status === "unlisted" ? "od-prop-status-tag--unlisted" : "od-prop-status-tag--active"}`}>
                          {property.isRented ? t("ownerDashboard.statusRented") : property.status === "unlisted" ? t("ownerDashboard.statusUnlisted") : t("ownerDashboard.statusActive")}
                        </span>
                      </div>
                      <div className="od-property-body">
                        <h4 className="od-property-name">{property.name || property.propertyName || "Untitled Property"}</h4>
                        <p className="od-property-location">{property.state || property.propertyState}, {property.city || property.propertyMunicipality || property.propertyDistrict}</p>
                        <div className="od-property-footer-row">
                          <span className="od-property-price">
                            {formatPrice(property.price || property.propertyPrice || property.rent || property.salePrice)}
                            <span className="od-price-per">{t("ownerDashboard.perMonth") || "/mo"}</span>
                          </span>
                          <div className="od-property-actions" onClick={(e) => e.stopPropagation()}>
                            {verificationStatusFromProperty(property) !== "approved" && (
                              <button
                                className="od-action-btn od-action-btn--verify"
                                onClick={() => openPropertyVerificationModal(property)}
                                title="Request property verification"
                                disabled={verificationStatusFromProperty(property) === "pending"}
                              >
                                <FontAwesomeIcon icon={verificationStatusFromProperty(property) === "pending" ? faClock : faShieldHalved} />
                              </button>
                            )}
                            {property.isRented && property.rentalInfo?.terminationRequested && (
                              <button className="od-action-btn od-action-btn--warn" onClick={() => handleViewTerminationRequest(property)} title={t("ownerDashboard.terminationPendingTitle") || "Termination pending"}>&#9888;&#65039;</button>
                            )}
                            {property.isRented && !property.rentalInfo?.terminationRequested && (
                              <button className="od-action-btn od-action-btn--ghost" onClick={() => handleRequestTermination(property.rentalInfo.rentalId)} title={t("ownerDashboard.endLease")}>
                                <FontAwesomeIcon icon={faKey} />
                              </button>
                            )}
                          </div>
                        </div>
                        {property.isRented && property.rentalInfo && (
                          <div className="od-rental-strip">
                            <span>🏠  {property.rentalInfo.renterName}</span>
                            <span>{t("ownerDashboard.untilLabel") || "Until"} {new Date(property.rentalInfo.endDate).toLocaleDateString("en-US", { month: "short", year: "numeric" })}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* â”€â”€ APPLICATIONS â”€â”€ */}
          {activeSection === "applications" && (
            <div className="od-section">
              <div className="od-filter-tabs">
                {["all", "pending", "approved", "rejected"].map((f) => (
                  <button key={f} className={`od-filter-tab${appStatusFilter === f ? " od-filter-tab--active" : ""}`} onClick={() => setAppStatusFilter(f)}>
                    {f === "all" ? t("ownerDashboard.filterAll") || "All" : f === "pending" ? t("ownerDashboard.filterPending") || "Pending" : f === "approved" ? t("ownerDashboard.filterApproved") || "Approved" : t("ownerDashboard.filterRejected") || "Rejected"}
                    {f === "pending" && pendingAppsCount > 0 && <span className="od-filter-badge od-filter-badge--red">{pendingAppsCount}</span>}
                    {f === "all" && applications.length > 0 && <span className="od-filter-badge od-filter-badge--grey">{applications.length}</span>}
                  </button>
                ))}
              </div>
              {filteredApps.length === 0 ? (
                <div className="od-empty-state">
                  <FontAwesomeIcon icon={faFileAlt} className="od-empty-icon" />
                  <h4>{t("ownerDashboard.noApplications")}</h4>
                  <p className="text-muted">{appStatusFilter === "all" ? t("ownerDashboard.noAppsFromRenters") || "Applications from renters will appear here." : t("ownerDashboard.noAppsFiltered") || "No applications at the moment for this filter."}</p>
                </div>
              ) : (
                <div className="od-app-cards">
                  {filteredApps.map((app) => (
                    <div key={app.id} className="od-app-card">
                      <div className="od-app-card-avatar">{(app.renterName || "?").charAt(0).toUpperCase()}</div>
                      <div className="od-app-card-body">
                        <div className="od-app-card-top">
                          <div>
                            <span className="od-app-card-name">{app.renterName}</span>
                            <span className="od-app-card-email">{app.renterEmail}</span>
                          </div>
                          <span className={`od-status-pill od-status-pill--${app.status}`}>{app.status.charAt(0).toUpperCase() + app.status.slice(1)}</span>
                        </div>
                        <div className="od-app-card-prop"><FontAwesomeIcon icon={faBuilding} className="me-1 text-muted" />{app.propertyName}</div>
                        <div className="od-app-card-meta">{t("ownerDashboard.appliedLabel") || "Applied"} {new Date(app.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</div>
                      </div>
                      <button className="od-app-card-cta" onClick={() => { setSelectedApplication(app); setShowApplicationModal(true); setShowRejectInput(false); setRejectReason(""); }}>
                        <FontAwesomeIcon icon={faEye} />
                        <span>{t("ownerDashboard.view")}</span>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* â”€â”€ ACTIVE RENTALS â”€â”€ */}
          {activeSection === "rentals" && (
            <div className="od-section">
              {rentedProperties.length === 0 ? (
                <div className="od-empty-state">
                  <FontAwesomeIcon icon={faKey} className="od-empty-icon" />
                  <h4>{t("ownerDashboard.noActiveRentals") || "No active rentals"}</h4>
                  <p className="text-muted">{t("ownerDashboard.noActiveRentalsMsg") || "When a property is rented it will appear here."}</p>
                </div>
              ) : (
                <div className="od-rental-list">
                  {rentedProperties.map((property) => (
                    <div key={property._id || property.id} className={`od-rental-card${property.rentalInfo?.terminationRequested ? " od-rental-card--alert" : ""}`}>
                      <img
                        src={property.thumbnail ? getPropertyImageUrl(property.thumbnail, { width: 200 }) : property.photos?.[0] ? getPropertyImageUrl(property.photos[0], { width: 200 }) : propertyThumbnail}
                        alt={property.name || property.propertyName}
                        className="od-rental-thumb"
                      />
                      <div className="od-rental-body">
                        <div className="od-rental-header-row">
                          <div>
                            <h4 className="od-rental-prop-name">{property.name || property.propertyName || "Untitled"}</h4>
                            <p className="od-rental-loc">{property.state || property.propertyState}, {property.city || property.propertyMunicipality}</p>
                          </div>
                          <span className="od-status-pill od-status-pill--rented">{t("ownerDashboard.statusRentedPill") || "Rented"}</span>
                        </div>
                        <div className="od-rental-details">
                          <div className="od-rental-detail">
                            <span className="od-rental-detail-label">{t("ownerDashboard.tenantLabel") || "Tenant"}</span>
                            <span className="od-rental-detail-val">{property.rentalInfo?.renterName || "â€”"}</span>
                          </div>
                          <div className="od-rental-detail">
                            <span className="od-rental-detail-label">{t("ownerDashboard.monthlyRentLabel") || "Monthly Rent"}</span>
                            <span className="od-rental-detail-val od-text-success">{formatPrice(property.rentalInfo?.monthlyRent || property.price)}</span>
                          </div>
                          <div className="od-rental-detail">
                            <span className="od-rental-detail-label">{t("ownerDashboard.leaseStartLabel") || "Lease Start"}</span>
                            <span className="od-rental-detail-val">{property.rentalInfo?.startDate ? new Date(property.rentalInfo.startDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "â€”"}</span>
                          </div>
                          <div className="od-rental-detail">
                            <span className="od-rental-detail-label">{t("ownerDashboard.leaseEndLabel") || "Lease End"}</span>
                            <span className="od-rental-detail-val">{property.rentalInfo?.endDate ? new Date(property.rentalInfo.endDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "â€”"}</span>
                          </div>
                        </div>
                        {property.rentalInfo?.terminationRequested ? (
                          <div className={`od-termination-banner ${property.rentalInfo.terminationRequestedBy !== currentUserId ? "od-termination-banner--danger" : "od-termination-banner--warn"}`}>
                            <span>{property.rentalInfo.terminationRequestedBy !== currentUserId ? `âš ï¸ ${t("ownerDashboard.renterRequestedTermination")}` : `â³ ${t("ownerDashboard.terminationPending")}`}</span>
                            <button className="od-termination-review-btn" onClick={() => handleViewTerminationRequest(property)}>{t("ownerDashboard.viewDetails")}</button>
                          </div>
                        ) : (
                          <div className="od-rental-action-row">
                            <button className="btn btn-sm btn-outline-danger" onClick={() => handleRequestTermination(property.rentalInfo?.rentalId)}>{t("ownerDashboard.endLease")}</button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* â”€â”€ APPLICATION FORM â”€â”€ */}
          {activeSection === "appform" && (
            <div className="od-section">
              <div className="od-card">
                <div className="od-card-header">
                  <h3 className="od-card-title">{t("ownerDashboard.appFormQuestions") || "Application Form Questions"}</h3>
                </div>
                <div className="od-card-body">
                  <div className="alert alert-info small mb-4">
                    {t("ownerDashboard.appFormStandardNote") || "The standard sections (Personal Info, Employment, Rental Details, References, Documents) are always included. Add extra questions below that renters must answer when applying to your properties."}
                  </div>
                  {customQuestions.length === 0 ? (
                    <p className="text-muted text-center py-3">{t("ownerDashboard.noCustomQuestions") || "No custom questions yet. Add one below."}</p>
                  ) : (
                    <div className="od-question-list mb-4">
                      {customQuestions.map((q, idx) => (
                        <div key={q.id || idx} className="od-question-row">
                          <span className="od-question-type">{q.type}</span>
                          <span className="od-question-label">{q.label}</span>
                          {q.required && <span className="badge bg-danger ms-1">{t("ownerDashboard.requiredLabel") || "Required"}</span>}
                          <button className="od-action-btn od-action-btn--ghost ms-auto" onClick={() => removeCustomQuestion(idx)} title="Remove">
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="od-add-question p-3 rounded border bg-light">
                    <h6 className="fw-semibold mb-3">{t("ownerDashboard.addAQuestion") || "Add a Question"}</h6>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label small">{t("ownerDashboard.questionLabelField") || "Question / Label"} <span className="text-danger">*</span></label>
                        <input type="text" className="form-control" placeholder="e.g. Do you smoke?" value={newQuestion.label} onChange={(e) => setNewQuestion((q) => ({ ...q, label: e.target.value }))} />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label small">{t("ownerDashboard.typeLabel") || "Type"}</label>
                        <select className="form-select" value={newQuestion.type} onChange={(e) => setNewQuestion((q) => ({ ...q, type: e.target.value }))}>
                          <option value="text">{t("ownerDashboard.shortText") || "Short Text"}</option>
                          <option value="textarea">{t("ownerDashboard.longText") || "Long Text"}</option>
                          <option value="number">{t("ownerDashboard.numberType") || "Number"}</option>
                          <option value="radio">{t("ownerDashboard.yesNo") || "Yes / No"}</option>
                          <option value="file">{t("ownerDashboard.fileUpload") || "File Upload"}</option>
                        </select>
                      </div>
                      <div className="col-md-3 d-flex align-items-end">
                        <div className="form-check">
                          <input type="checkbox" className="form-check-input" id="newQRequired" checked={newQuestion.required} onChange={(e) => setNewQuestion((q) => ({ ...q, required: e.target.checked }))} />
                          <label className="form-check-label" htmlFor="newQRequired">{t("ownerDashboard.requiredLabel") || "Required"}</label>
                        </div>
                      </div>
                      <div className="col-12 d-flex gap-2">
                        <button className="btn btn-outline-primary btn-sm" onClick={addCustomQuestion}><FontAwesomeIcon icon={faPlus} className="me-1" />{t("ownerDashboard.addQuestion") || "Add Question"}</button>
                        <button className="btn btn-primary btn-sm" onClick={handleSaveTemplate} disabled={savingTemplate}>
                          {savingTemplate ? <span className="spinner-border spinner-border-sm me-1" role="status" /> : null}
                          {t("ownerDashboard.saveTemplate") || "Save Template"}
                      </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* â”€â”€ ACCOUNT SETTINGS â”€â”€ */}
          {activeSection === "settings" && (
            <div className="od-section">
              <div className="od-settings-grid">
                <div className="od-card">
                  <div className="od-card-header"><h3 className="od-card-title">{t("ownerDashboard.profileSection") || "Profile"}</h3></div>
                  <div className="od-card-body">
                    <div className="od-profile-hero">
                      <div className="od-profile-avatar">{ownerName.charAt(0).toUpperCase() || "O"}</div>
                      <div>
                        <div className="od-profile-name">{ownerName}</div>
                        <div className="od-profile-role">{t("ownerDashboard.propertyOwnerRole") || "Property Owner"}</div>
                      </div>
                    </div>
                    <div className="od-settings-field"><span className="od-settings-label">{t("ownerDashboard.fullNameLabel") || "Full Name"}</span><span className="od-settings-val">{ownerName}</span></div>
                    <div className="od-settings-field"><span className="od-settings-label">{t("ownerDashboard.accountRoleLabel") || "Account Role"}</span><span className="od-settings-val">{t("ownerDashboard.propertyOwnerRole") || "Property Owner"}</span></div>
                    <button className="btn btn-outline-primary btn-sm mt-3" onClick={() => navigate("/profile")}>{t("ownerDashboard.editProfile") || "Edit Profile"}</button>
                  </div>
                </div>

                <div className="od-card">
                  <div className="od-card-header"><h3 className="od-card-title">{t("ownerDashboard.identityVerificationTitle") || "Identity Verification (KYC)"}</h3></div>
                  <div className="od-card-body">
                    <div className={`od-kyc-block od-kyc-block--${kycStatus || "not_submitted"}`}>
                      <FontAwesomeIcon icon={faShieldHalved} className="od-kyc-block-icon" />
                      <div>
                        <div className="od-kyc-block-title">
                          {kycStatus === "approved" ? t("ownerDashboard.identityVerifiedStatus") || "Identity Verified" : kycStatus === "pending" ? t("ownerDashboard.underReviewStatus") || "Under Review" : kycStatus === "rejected" ? t("ownerDashboard.verificationRejectedStatus") || "Verification Rejected" : t("ownerDashboard.notSubmittedStatus") || "Not Submitted"}
                        </div>
                        <div className="od-kyc-block-desc">
                          {kycStatus === "approved" ? t("ownerDashboard.kycVerifiedDesc") || "Your identity has been verified. You have full access to all owner features." : kycStatus === "pending" ? t("ownerDashboard.kycUnderReviewDesc") || "Your documents are being reviewed. This usually takes 1\u20132 business days." : kycStatus === "rejected" ? t("ownerDashboard.kycRejectedDesc") || "Your submission was rejected. Please resubmit with correct documents." : t("ownerDashboard.kycNotSubmittedDesc") || "Submit your identity documents to unlock property listing and other features."}
                        </div>
                      </div>
                    </div>
                    {kycStatus !== "approved" && (
                      <button className="btn btn-primary btn-sm mt-3" onClick={() => navigate("/kyc-verification")}>
                        {kycStatus === "pending" ? t("ownerDashboard.checkStatus") || "Check Status" : kycStatus === "rejected" ? t("ownerDashboard.resubmitKyc") || "Resubmit KYC" : t("ownerDashboard.startKyc") || "Start KYC"}
                      </button>
                    )}
                  </div>
                </div>

                <div className="od-card">
                  <div className="od-card-header"><h3 className="od-card-title">{t("ownerDashboard.securitySection") || "Security"}</h3></div>
                  <div className="od-card-body">
                    <div className="od-settings-field"><span className="od-settings-label">{t("ownerDashboard.passwordLabel") || "Password"}</span><span className="od-settings-val">â€¢â€¢â€¢â€¢â€¢â€¢â€¢â€¢â€¢â€¢â€¢â€¢</span></div>
                    <button className="btn btn-outline-secondary btn-sm mt-3" onClick={() => navigate("/profile")}>{t("ownerDashboard.changePassword") || "Change Password"}</button>
                  </div>
                </div>

                <div className="od-card od-card--danger-zone">
                  <div className="od-card-header"><h3 className="od-card-title od-card-title--danger">{t("ownerDashboard.dangerZone") || "Danger Zone"}</h3></div>
                  <div className="od-card-body">
                    <p className="text-muted small mb-3">{t("ownerDashboard.signOutMsg") || "Signing out will clear your local session data."}</p>
                    <button className="btn btn-outline-danger btn-sm" onClick={() => { localStorage.clear(); navigate("/login"); }}>
                      <FontAwesomeIcon icon={faRightFromBracket} className="me-1" /> Sign Out
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

        </main>
      </div>

      {/* â”€â”€ Mobile bottom nav â”€â”€ */}
      <nav className="od-bottom-nav" aria-label="Mobile navigation">
        <button className={`od-bnav-btn${activeSection === "overview" ? " od-bnav-btn--active" : ""}`} onClick={() => navTo("overview")}>
          <FontAwesomeIcon icon={faGaugeHigh} /><span>{t("ownerDashboard.overviewTab") || "Overview"}</span>
        </button>
        <button className={`od-bnav-btn${activeSection === "properties" ? " od-bnav-btn--active" : ""}`} onClick={() => navTo("properties")}>
          <FontAwesomeIcon icon={faBuilding} /><span>{t("ownerDashboard.propertiesTab") || "Properties"}</span>
        </button>
        <button className="od-bnav-center" onClick={() => navigate("/list-property")} aria-label="Add property">
          <FontAwesomeIcon icon={faPlus} />
        </button>
        <button className={`od-bnav-btn${activeSection === "applications" ? " od-bnav-btn--active" : ""}`} onClick={() => navTo("applications")}>
          <div className="od-bnav-icon-wrap">
            <FontAwesomeIcon icon={faFileAlt} />
            {pendingAppsCount > 0 && <span className="od-bnav-badge">{pendingAppsCount}</span>}
          </div>
          <span>{t("ownerDashboard.appsTab") || "Apps"}</span>
        </button>
        <button className={`od-bnav-btn${activeSection === "settings" ? " od-bnav-btn--active" : ""}`} onClick={() => navTo("settings")}>
          <FontAwesomeIcon icon={faCog} /><span>{t("ownerDashboard.moreTab") || "More"}</span>
        </button>
      </nav>

      {/* â•â• Modals â•â• */}

      {showPropertyVerificationModal && verificationProperty && (
        <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.62)" }} onClick={() => setShowPropertyVerificationModal(false)}>
          <div className="modal-dialog modal-lg modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <FontAwesomeIcon icon={faShieldHalved} className="me-2" />
                  Verify Property - {verificationProperty.name || verificationProperty.propertyName}
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowPropertyVerificationModal(false)} />
              </div>
              <div className="modal-body">
                <div className="od-verify-hero">
                  <h6>Build renter trust and unlock your verified badge</h6>
                  <p>
                    Verified properties earn more confidence from renters and reduce fraud risk.
                    Submit your Nepal property proof once and get reviewed by the admin team.
                  </p>
                </div>

                <div className="od-verify-grid">
                  {(verificationRequirements.length > 0 ? verificationRequirements : [
                    { field: "ownership_certificate", label: "Ownership Certificate (Lalpurja)", required: true },
                    { field: "tax_clearance_certificate", label: "Property Tax or Ward Clearance", required: true },
                    { field: "land_deed_certificate", label: "Land Deed / Transfer Document", required: false },
                    { field: "utility_bill_certificate", label: "Recent Utility Bill", required: false },
                  ]).map((doc) => (
                    <div key={doc.field} className="od-verify-doc-row">
                      <label className="form-label small fw-semibold mb-1">
                        {doc.label} {doc.required ? <span className="text-danger">*</span> : null}
                      </label>
                      <input
                        type="file"
                        className="form-control"
                        accept=".jpg,.jpeg,.png,.webp,.pdf"
                        onChange={(e) => handlePropertyVerificationFile(doc.field, e.target.files?.[0] || null)}
                      />
                    </div>
                  ))}
                </div>

                <div className="mt-3">
                  <label className="form-label small fw-semibold mb-1">Additional Notes (optional)</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    placeholder="Add any context for admin review, such as ownership details or municipal references."
                    value={verificationForm.supportingNotes}
                    onChange={(e) => setVerificationForm((prev) => ({ ...prev, supportingNotes: e.target.value }))}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowPropertyVerificationModal(false)} disabled={verificationSubmitting}>Cancel</button>
                <button className="btn btn-primary" onClick={submitPropertyVerification} disabled={verificationSubmitting}>
                  {verificationSubmitting ? <span className="spinner-border spinner-border-sm me-2" /> : <FontAwesomeIcon icon={faCheck} className="me-2" />}
                  Submit Verification Request
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showTerminationModal && selectedTermination && (
        <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }} onClick={() => setShowTerminationModal(false)}>
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className={`modal-header ${selectedTermination.requestedBy !== currentUserId ? "bg-danger text-white" : "bg-warning"}`}>
                <h5 className="modal-title">{selectedTermination.requestedBy !== currentUserId ? `âš ï¸ ${t("ownerDashboard.renterRequestedModal")}` : t("ownerDashboard.ownerRequestedModal")}</h5>
                <button type="button" className={`btn-close ${selectedTermination.requestedBy !== currentUserId ? "btn-close-white" : ""}`} onClick={() => setShowTerminationModal(false)} />
              </div>
              <div className="modal-body">
                <div className="mb-3"><label className="form-label fw-semibold">{t("ownerDashboard.property")}</label><p className="text-muted">{selectedTermination.propertyName}</p></div>
                <div className="mb-3"><label className="form-label fw-semibold">{selectedTermination.requestedBy !== currentUserId ? t("ownerDashboard.renterLabel") : t("ownerDashboard.currentTenant")}</label><p className="text-muted">{selectedTermination.renterName}</p></div>
                <div className="mb-3"><label className="form-label fw-semibold">{t("ownerDashboard.reasonForTermination")}</label><div className="alert alert-light border">{selectedTermination.reason || t("ownerDashboard.noReasonProvided")}</div></div>
                {selectedTermination.requestedBy !== currentUserId && (<div className="alert alert-warning"><small><strong>Note:</strong> {t("ownerDashboard.terminationNoteRenter")}</small></div>)}
                {selectedTermination.requestedBy === currentUserId && (<div className="alert alert-info"><small><strong>Note:</strong> {t("ownerDashboard.terminationNoteOwner")}</small></div>)}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowTerminationModal(false)}>{t("ownerDashboard.close")}</button>
                {selectedTermination.requestedBy !== currentUserId ? (
                  <>
                    <button type="button" className="btn btn-outline-danger" onClick={() => { setShowTerminationModal(false); setShowDenyModal(true); }}>{t("ownerDashboard.denyRequest")}</button>
                    <button type="button" className="btn btn-danger" onClick={() => handleApproveTermination(selectedTermination.rentalId)}>{t("ownerDashboard.approveTermination")}</button>
                  </>
                ) : (
                  <button type="button" className="btn btn-warning" onClick={() => handleCancelTermination(selectedTermination.rentalId)}>{t("ownerDashboard.cancelRequest")}</button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showDenyModal && selectedTermination && (
        <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }} onClick={() => setShowDenyModal(false)}>
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header bg-warning">
                <h5 className="modal-title">{t("ownerDashboard.denyTerminationTitle")}</h5>
                <button type="button" className="btn-close" onClick={() => setShowDenyModal(false)} />
              </div>
              <div className="modal-body">
                <div className="mb-3"><label className="form-label fw-semibold">{t("ownerDashboard.property")}</label><p className="text-muted">{selectedTermination.propertyName}</p></div>
                <div className="mb-3"><label className="form-label fw-semibold">{t("ownerDashboard.renterLabel")}</label><p className="text-muted">{selectedTermination.renterName}</p></div>
                <div className="alert alert-warning"><small><strong>Note:</strong> {t("ownerDashboard.denyNote")}</small></div>
                <div className="mb-3">
                  <label className="form-label fw-semibold">{t("ownerDashboard.reasonForDenial")} <span className="text-danger">*</span></label>
                  <textarea className="form-control" rows="4" placeholder={t("ownerDashboard.denialPlaceholder")} value={denyReason} onChange={(e) => setDenyReason(e.target.value)} />
                  <small className="text-muted">{t("ownerDashboard.denyingHint")}</small>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => { setShowDenyModal(false); setDenyReason(""); }} disabled={submittingDeny}>{t("ownerDashboard.cancel")}</button>
                <button type="button" className="btn btn-warning" onClick={handleDenyTermination} disabled={submittingDeny || !denyReason.trim()}>
                  {submittingDeny ? (<><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />{t("ownerDashboard.denying")}</>) : t("ownerDashboard.denyRequest")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showApplicationModal && selectedApplication && (
        <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.6)" }} onClick={() => setShowApplicationModal(false)}>
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title"><FontAwesomeIcon icon={faFileAlt} className="me-2" />Application â€” {selectedApplication.propertyName}</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowApplicationModal(false)} />
              </div>
              <div className="modal-body">
                <div className="mb-4 p-3 bg-light rounded">
                  <h6 className="fw-bold mb-2">{t("ownerDashboard.applicant")}</h6>
                  <div className="row g-2">
                    <div className="col-6"><small className="text-muted">{t("ownerDashboard.name")}</small><div>{selectedApplication.renterName}</div></div>
                    <div className="col-6"><small className="text-muted">{t("ownerDashboard.email")}</small><div>{selectedApplication.renterEmail}</div></div>
                    <div className="col-6"><small className="text-muted">{t("ownerDashboard.phone")}</small><div>{selectedApplication.renterPhone || "â€”"}</div></div>
                    <div className="col-6"><small className="text-muted">{t("ownerDashboard.colSubmitted")}</small><div>{new Date(selectedApplication.createdAt).toLocaleDateString()}</div></div>
                  </div>
                </div>
                {Object.keys(selectedApplication.answers).length > 0 && (
                  <div className="mb-4">
                    <h6 className="fw-bold mb-3">{t("ownerDashboard.appAnswers")}</h6>
                    <div className="row g-3">
                      {Object.entries(selectedApplication.answers).map(([key, val]) => (
                        <div key={key} className="col-md-6">
                          <small className="text-muted text-uppercase" style={{ fontSize: "0.7rem", letterSpacing: "0.05em" }}>{key.replace(/_/g, " ")}</small>
                          <div className="fw-semibold">{val || "â€”"}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {Object.keys(selectedApplication.documents).length > 0 && (
                  <div className="mb-4">
                    <h6 className="fw-bold mb-3">{t("ownerDashboard.submittedDocs")}</h6>
                    <div className="d-flex flex-wrap gap-2">
                      {Object.entries(selectedApplication.documents).map(([field, filename]) => (
                        <button key={field} type="button" className="btn btn-sm btn-outline-primary" onClick={() => handlePreviewDocument(field, filename)} disabled={previewLoading}>
                          <FontAwesomeIcon icon={faFileAlt} className="me-1" />
                          {previewLoading ? t("ownerDashboard.previewLoadingLabel") || "Loading..." : `${t("ownerDashboard.previewBtn") || "Preview"} ${field.replace(/_/g, " ")}`}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {showRejectInput && (
                  <div className="mb-3">
                    <label className="form-label fw-semibold">{t("ownerDashboard.reasonForRejection")} <span className="text-danger">*</span></label>
                    <textarea className="form-control" rows={3} placeholder={t("ownerDashboard.rejectPlaceholder") || "Explain why you are rejecting this application..."} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
                  </div>
                )}
                {selectedApplication.status === "rejected" && selectedApplication.rejectionReason && (
                  <div className="alert alert-danger"><strong>{t("ownerDashboard.rejectionReasonLabel")}</strong> {selectedApplication.rejectionReason}</div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowApplicationModal(false)}>{t("ownerDashboard.close")}</button>
                {selectedApplication.status === "pending" && !showRejectInput && (
                  <>
                    <button className="btn btn-danger" onClick={() => setShowRejectInput(true)} disabled={processingApp}><FontAwesomeIcon icon={faTimes} className="me-1" />{t("ownerDashboard.reject")}</button>
                    <button className="btn btn-success" onClick={() => handleApproveApplication(selectedApplication.id)} disabled={processingApp}>
                      {processingApp ? <span className="spinner-border spinner-border-sm me-1" /> : <FontAwesomeIcon icon={faCheck} className="me-1" />}
                      {t("ownerDashboard.approveCreateRental")}
                    </button>
                  </>
                )}
                {showRejectInput && (
                  <>
                    <button className="btn btn-outline-secondary" onClick={() => { setShowRejectInput(false); setRejectReason(""); }}>{t("ownerDashboard.cancel")}</button>
                    <button className="btn btn-danger" onClick={() => handleRejectApplication(selectedApplication.id)} disabled={processingApp || !rejectReason.trim()}>
                      {processingApp ? <span className="spinner-border spinner-border-sm me-1" /> : null}
                      {t("ownerDashboard.confirmRejection")}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {previewDocUrl && (
        <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.7)" }} onClick={closeDocumentPreview}>
          <div className="modal-dialog modal-xl modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title text-capitalize">{t("ownerDashboard.docPreviewTitle")} â€” {previewDocLabel}</h5>
                <button type="button" className="btn-close" onClick={closeDocumentPreview} />
              </div>
              <div className="modal-body" style={{ minHeight: "70vh" }}>
                {previewDocType === "image" && (<img src={previewDocUrl} alt={previewDocLabel} style={{ maxWidth: "100%", maxHeight: "68vh", display: "block", margin: "0 auto" }} />)}
                {previewDocType === "pdf" && (<iframe title={previewDocLabel} src={previewDocUrl} style={{ width: "100%", height: "68vh", border: 0 }} />)}
                {previewDocType === "other" && (<div className="alert alert-secondary mb-0">{t("ownerDashboard.previewNotSupported")}</div>)}
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default OwnerDashboard;
